/**
 * Every page's title and description, in both languages.
 *
 * Shared for the same reason src/shared/language.ts is: both halves have to
 * give the same answer. The server writes these into the HTML it sends, and
 * <Seo> re-applies them once React is running. Two copies of the copy would
 * eventually disagree, and the symptom of that disagreement is a page telling
 * Google one thing and a reader another.
 *
 * ── Why the server needs this at all ───────────────────────────────────────
 * This is a client-rendered SPA: before this file existed, every URL was
 * answered with the same index.html, so /es, /support and /es/terms all
 * shipped the front page's <title>, its description and — the expensive one —
 * its <link rel="canonical">. Every page declared itself a duplicate of the
 * home page until JavaScript ran. Google renders JS and mostly recovered, but
 * "mostly" is the problem: Search Console reported a page dropped as
 * "Alternate page with proper canonical tag", which is Google believing the
 * tag we did not mean to send.
 *
 * Keep this free of JSX, of runtime dependencies and of anything Node-specific
 * — it is compiled into the server bundle AND into the browser bundle.
 */
import { DEFAULT_LOCALE, isLocale, type Locale } from './waitlist.js';
import { stripLocale } from './language.js';

/**
 * The address that actually serves the site, and therefore what canonical
 * tags, hreflang and Open Graph URLs must point at.
 *
 * www, not the bare domain, because rickey.app redirects to it. A canonical
 * URL that redirects wastes a hop and muddies the signal.
 *
 * src/client/lib/constants.ts re-exports this rather than declaring its own,
 * so the browser and the server cannot drift onto different origins.
 */
export const SITE_ORIGIN = 'https://www.rickey.app';

/** What the head needs to describe one page in one language. */
export interface PageSeo {
  title: string;
  description: string;
}

/**
 * Every indexable path, without a locale prefix.
 *
 * This is the same list as PAGES in src/client/App.tsx and the same list as
 * public/sitemap.xml. Adding a page means a line in all three; a test asserts
 * this array and the sitemap agree, so the third one cannot be forgotten.
 */
export const PAGE_PATHS = [
  '/',
  '/privacy',
  '/terms',
  '/cookies',
  '/support',
  '/delete-account',
] as const;

export type PagePath = (typeof PAGE_PATHS)[number];

/**
 * The copy itself.
 *
 * Spanish is written natively rather than translated, so the two halves of a
 * row are not word-for-word equivalents and are not meant to be.
 *
 * Titles carry the " — Rickey" suffix except on the front page, whose title
 * already names the product. The suffix is what makes a bare search result
 * legible when the page name alone ("Support", "Soporte") says nothing about
 * whose support it is.
 */
const PAGES: Record<PagePath, Record<Locale, PageSeo>> = {
  '/': {
    en: {
      title: 'Rickey — tonight’s menu, from your shelf',
      description:
        'Tell Rickey what you own and it reads back what you can pour tonight — measures, order and timings, scaled to how many you are making.',
    },
    es: {
      title: 'Rickey — la carta de esta noche, de tu propia barra',
      description:
        'Dile a Rickey qué tienes y te devuelve lo que puedes servir esta noche: medidas, orden y tiempos, ajustados a cuántos vas a servir.',
    },
  },

  '/privacy': {
    en: {
      title: 'Privacy Policy — Rickey',
      description: 'What personal data Rickey collects, why, and how to exercise your rights.',
    },
    es: {
      title: 'Aviso de Privacidad — Rickey',
      description: 'Qué datos personales recaba Rickey, para qué, y cómo ejercer tus derechos.',
    },
  },

  '/terms': {
    en: {
      title: 'Terms of Service — Rickey',
      description: 'The terms of the agreement between you and BuildPassing for using Rickey.',
    },
    es: {
      title: 'Términos del Servicio — Rickey',
      description: 'Los términos del acuerdo entre tú y BuildPassing para el uso de Rickey.',
    },
  },

  '/cookies': {
    en: {
      title: 'Cookie Policy — Rickey',
      description:
        'Rickey does not use cookies. This page explains exactly what is stored and what is not.',
    },
    es: {
      title: 'Política de Cookies — Rickey',
      description: 'Rickey no usa cookies. Esta página explica exactamente qué se guarda y qué no.',
    },
  },

  '/support': {
    en: {
      title: 'Support — Rickey',
      description:
        'Something broken, something confusing, or something you wish it did? Write to us. A person reads every message, and we answer.',
    },
    es: {
      title: 'Soporte — Rickey',
      description:
        '¿Algo se rompió, algo confunde, o algo te gustaría que hiciera? Escríbenos. Una persona lee cada mensaje, y respondemos.',
    },
  },

  '/delete-account': {
    en: {
      title: 'Delete your account — Rickey',
      description:
        'You can delete your Rickey account and everything attached to it at any time. You do not need our permission and you do not need to explain why.',
    },
    es: {
      title: 'Eliminar tu cuenta — Rickey',
      description:
        'Puedes eliminar tu cuenta de Rickey y todo lo asociado a ella en cualquier momento. No necesitas nuestro permiso ni tienes que explicar por qué.',
    },
  },
};

/** Whether a locale-free path is one of the real pages. */
export function isPagePath(path: string): path is PagePath {
  return Object.prototype.hasOwnProperty.call(PAGES, path);
}

/**
 * Split a request path into the language it asks for and the page it names.
 *
 * A trailing slash is not a different page: /es/ and /es are the same request,
 * and answering them with different canonicals is one of the ways a site
 * competes with itself. Normalising here means the server, the client and the
 * tests cannot disagree about it.
 */
export function routeFor(pathname: string): { locale: Locale; path: string } {
  const first = pathname.split('/').filter(Boolean)[0];
  const locale: Locale = isLocale(first) ? first : DEFAULT_LOCALE;
  return { locale, path: stripLocale(pathname) };
}

/** The strings for one page in one language, or null if the path is not a page. */
export function seoFor(path: string, locale: Locale): PageSeo | null {
  if (!isPagePath(path)) return null;
  return PAGES[path][locale] ?? null;
}

/** Where a locale-free path lives in a given language. */
export function pathFor(path: PagePath, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return path;
  return path === '/' ? '/es' : `/es${path}`;
}

/** The absolute URL a page should name as its own. */
export function canonicalFor(path: PagePath, locale: Locale): string {
  return `${SITE_ORIGIN}${pathFor(path, locale)}`;
}

/**
 * The hreflang set for a page.
 *
 * x-default names the version to serve anyone the other two do not cover,
 * which is English — the same answer the language middleware falls back to.
 */
export function alternatesFor(path: PagePath): { hreflang: string; href: string }[] {
  const en = canonicalFor(path, 'en');
  return [
    { hreflang: 'en', href: en },
    { hreflang: 'es', href: canonicalFor(path, 'es') },
    { hreflang: 'x-default', href: en },
  ];
}

/**
 * The share card.
 *
 * One image per language, because it carries a line of copy and the brand rule
 * is one language per piece.
 */
export function ogImageFor(locale: Locale): string {
  return `${SITE_ORIGIN}${locale === 'es' ? '/og-image-es.png' : '/og-image.png'}`;
}

/** The Open Graph locale code, which is not the same shape as ours. */
export function ogLocaleFor(locale: Locale): string {
  return locale === 'es' ? 'es_MX' : 'en_US';
}
